import React, { useEffect, useState } from "react";
import { adminApi } from "services/api";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import SaveIcon from "@mui/icons-material/Save";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useAuth } from "context/AuthContext";
import { blueGrey } from "@mui/material/colors";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDSnackbar from "components/MDSnackbar";
import { lightDark, darkTint, DARK } from "utils/lightDark";
import { withDrafts, editDraft, clearDraft, hasDraft } from "./permissionDrafts";

// ── Permission groups ─────────────────────────────────────────────────────────
// Mirrors routes.js — update here whenever a new route is added.

const PAGE_GROUPS = [
  {
    label: "Payments",
    icon: "account_balance_wallet",
    color: "#1a237e",
    darkColor: "#9fa8da",
    pages: [
      { key: "payment-management", label: "Payment Management" },
      { key: "payment-link-generator", label: "Payment Links" },
    ],
  },
  {
    label: "Blog",
    icon: "article",
    color: "#4a148c",
    darkColor: "#ce93d8",
    pages: [
      { key: "blog-categories", label: "Blog Categories" },
      { key: "blog-posts", label: "Blog Posts" },
    ],
  },
  {
    label: "Content",
    icon: "edit_note",
    color: "#004d40",
    darkColor: "#80cbc4",
    pages: [
      { key: "services", label: "Services" },
      { key: "bootcamps", label: "Bootcamps" },
      { key: "team-members", label: "Team Members" },
      { key: "testimonials", label: "Testimonials" },
      { key: "pages", label: "Website Pages" },
      { key: "settings", label: "Settings" },
    ],
  },
  {
    label: "Submissions",
    icon: "inbox",
    color: "#bf360c",
    darkColor: "#ffab91",
    pages: [
      { key: "webinar-registrations", label: "Webinar Registrations" },
      { key: "landing-applications", label: "Landing Applications" },
      { key: "contact-submissions", label: "Contact Submissions" },
      { key: "bootcamp-applications", label: "Bootcamp Applications" },
      // Also grants the Meetings page — the two are closely related and
      // share this one toggle rather than each getting its own.
      { key: "consultations", label: "Consultations & Meetings" },
      { key: "clarity-call-bookings", label: "Clarity Call Bookings" },
      { key: "newsletter", label: "Newsletter" },
      { key: "customer-chat", label: "Customer Chat (SMS/WhatsApp)" },
      { key: "voice-calls", label: "Voice Calling" },
    ],
  },
  {
    label: "Tools",
    icon: "build",
    color: "#e65100",
    darkColor: "#ffb74d",
    pages: [
      // Also grants Bulk Message — shares this toggle rather than having its own.
      { key: "autoresponder", label: "Autoresponder & Bulk Message" },
      { key: "file-manager", label: "File Library" },
    ],
  },
  {
    label: "System",
    icon: "tune",
    color: "#37474f",
    darkColor: "#b0bec5",
    pages: [{ key: "notifications", label: "Notifications" }],
  },
];

// Keys that are always granted — cannot be revoked. Must match the
// always-granted list in context/AuthContext.js's hasPermission() exactly.
const DEFAULT_PAGES = ["dashboard", "profile", "logout", "timesheet"];

// Flat list of all controllable keys (for save / toggle-all)
const ALL_PERMISSION_KEYS = PAGE_GROUPS.flatMap((g) => g.pages.map((p) => p.key));

// Finger-sized targets below md: small buttons are 32px and small chips 24px tall.
const touchButtonSx = ({ breakpoints }) => ({ [breakpoints.down("md")]: { minHeight: 40 } });
const permissionChipsSx = ({ breakpoints }) => ({
  [breakpoints.down("md")]: { gap: 1, "& .MuiChip-root": { height: 32 } },
});

// ─────────────────────────────────────────────────────────────────────────────

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // userId being saved
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState(null);
  const [addUserForm, setAddUserForm] = useState({ name: "", email: "", isGlobalAdmin: false });
  const [resending, setResending] = useState(null); // userId currently being resent
  const [accessTarget, setAccessTarget] = useState(null); // user awaiting disable/enable confirmation
  const [accessSaving, setAccessSaving] = useState(false);
  // Unsaved card edits by user id, kept out of `users` so paging (which reloads it) keeps them.
  const [drafts, setDrafts] = useState({});
  const [notice, setNotice] = useState(null); // { title, content } success message
  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetchUsers(page);
  }, [page]); // eslint-disable-line

  // Closing or reloading the tab would lose unsaved edits, so the browser asks first.
  const unsavedCount = Object.keys(drafts).length;
  useEffect(() => {
    if (!unsavedCount) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsavedCount]);

  const fetchUsers = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await adminApi.getUsers({ page: pageNum, limit: 8 });
      setUsers(res.users);
      setTotalPages(res.totalPages);
      setError(null);
    } catch {
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // ── Add user ───────────────────────────────────────────────────────────────
  const handleAddUserOpen = () => {
    setAddUserForm({ name: "", email: "", isGlobalAdmin: false });
    setAddUserError(null);
    setAddUserOpen(true);
  };
  const handleAddUserClose = () => setAddUserOpen(false);
  const handleAddUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddUserForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };
  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    if (!addUserForm.name.trim()) {
      setAddUserError("Full name is required");
      return;
    }
    setAddUserLoading(true);
    setAddUserError(null);
    try {
      await adminApi.inviteUser(addUserForm);
      setAddUserOpen(false);
      setNotice({
        title: "Invite sent",
        content: `An invite email is on its way to ${addUserForm.email.trim()}.`,
      });
      fetchUsers(page);
    } catch (err) {
      // The server explains a failed invite email: normally the account was removed again and
      // they can just retry; if it couldn't be removed it says so and points at Resend Invite,
      // so reload the list to show that account.
      setAddUserError(err?.response?.data?.error || "Failed to send invite");
      if (err?.response?.status === 502) fetchUsers(page);
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleResendInvite = async (user) => {
    setResending(user._id);
    try {
      await adminApi.resendInvite(user._id);
      setError(null);
      setNotice({
        title: "Invite sent",
        content: `A new invite email is on its way to ${user.email}.`,
      });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to resend invite");
    } finally {
      setResending(null);
    }
  };

  // ── Disable / enable ───────────────────────────────────────────────────────
  const handleConfirmAccess = async () => {
    const target = accessTarget;
    setAccessSaving(true);
    try {
      const updated = await adminApi.setUserDisabled(target._id, !target.disabled);
      setUsers((prev) => prev.map((u) => (u._id === target._id ? { ...u, ...updated } : u)));
      setError(null);
      setAccessTarget(null);
    } catch (err) {
      setError(
        err?.response?.data?.error || `Failed to ${target.disabled ? "enable" : "disable"} user`
      );
      setAccessTarget(null);
    } finally {
      setAccessSaving(false);
    }
  };

  // ── Permission helpers ─────────────────────────────────────────────────────
  // `user` is the card's user with its draft applied. Edits land in `drafts` and are compared
  // with the saved copy in `users`, so undoing an edit clears the "Unsaved changes" mark.
  const editUser = (user, changes) => {
    const saved = users.find((u) => u._id === user._id);
    setDrafts((prev) => editDraft(prev, user, changes, saved));
  };

  const handlePermissionChange = (user, key, checked) => {
    editUser(user, { permissions: { ...user.permissions, [key]: checked } });
  };

  const handleToggleAll = (user, grant) => {
    const perms = {};
    ALL_PERMISSION_KEYS.forEach((k) => {
      perms[k] = grant;
    });
    editUser(user, { permissions: perms });
  };

  const handleGlobalAdminChange = (user, checked) => editUser(user, { isGlobalAdmin: checked });

  const handleNotifyToggleChange = (user, checked) =>
    editUser(user, { notifyOnTwilioMessage: checked });

  const handleMissedCallNotifyToggleChange = (user, checked) =>
    editUser(user, { notifyOnMissedCall: checked });

  const handleSave = async (user) => {
    setSaving(user._id);
    const { permissions, isGlobalAdmin, notifyOnTwilioMessage, notifyOnMissedCall } = user;
    try {
      const updated = await adminApi.updateUserPermissions(
        user._id,
        permissions,
        isGlobalAdmin,
        notifyOnTwilioMessage,
        notifyOnMissedCall
      );
      setUsers((prev) =>
        prev.map((u) =>
          u._id === user._id
            ? {
                ...u,
                ...(updated || {}),
                permissions,
                isGlobalAdmin,
                notifyOnTwilioMessage,
                notifyOnMissedCall,
              }
            : u
        )
      );
      setDrafts((prev) => clearDraft(prev, user._id));
      setError(null);
      setNotice({
        title: "Permissions saved",
        content: `${user.name || user.email}'s access has been updated.`,
      });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save permissions");
    } finally {
      setSaving(null);
    }
  };

  const shownUsers = withDrafts(users, drafts);
  const unsavedElsewhere = Object.keys(drafts).filter(
    (id) => !users.some((u) => u._id === id)
  ).length;

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress color="info" />
        </MDBox>
      </DashboardLayout>
    );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        pt={4}
        pb={6}
        px={{ xs: 0, sm: 2 }}
        sx={{ minHeight: "100vh", background: lightDark("#f8fafc", "transparent") }}
      >
        <MDTypography variant="h4" fontWeight="bold" mb={1} color="info.main" textAlign="center">
          Manage Users & Permissions
        </MDTypography>
        <MDTypography variant="body2" color="text" textAlign="center" mb={3}>
          Global Admins have unrestricted access. For all other users, enable only the pages they
          need.
        </MDTypography>

        <MDBox display="flex" justifyContent="flex-end" mb={3}>
          <MDButton color="info" variant="gradient" onClick={handleAddUserOpen}>
            <Icon sx={{ mr: 0.5 }}>mail</Icon>Invite User
          </MDButton>
        </MDBox>

        {error && (
          <MDTypography color="error" mb={2} textAlign="center">
            {error}
          </MDTypography>
        )}

        <Grid container spacing={3}>
          {shownUsers.map((user) => {
            const isGlobal = !!user.isGlobalAdmin;
            const isSelf = currentUser?._id === user._id;
            const grantedCount = ALL_PERMISSION_KEYS.filter((k) => user.permissions?.[k]).length;

            return (
              <Grid item xs={12} md={6} key={user._id}>
                <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3 }}>
                  {/* User header — below lg the card is too narrow for name, email and chips
                      side by side, so the chips drop to their own row under the name. */}
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={2}
                    rowGap={1}
                    mb={2}
                    flexWrap={{ xs: "wrap", lg: "nowrap" }}
                  >
                    <Avatar sx={{ bgcolor: blueGrey[700], width: 52, height: 52, fontSize: 22 }}>
                      {(user.name || user.email)[0].toUpperCase()}
                    </Avatar>
                    <MDBox flex={1} minWidth={0}>
                      <MDTypography fontWeight="bold" fontSize={16}>
                        {user.name || "—"}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {user.email}
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      textAlign="right"
                      sx={{
                        display: { xs: "flex", lg: "block" },
                        flexWrap: "wrap",
                        gap: 0.5,
                        width: { xs: "100%", lg: "auto" },
                        pl: { xs: "68px", lg: 0 },
                      }}
                    >
                      {user.disabled && (
                        <Chip
                          label="Disabled"
                          size="small"
                          color="error"
                          sx={{
                            fontWeight: 700,
                            mb: { xs: 0, lg: 0.5 },
                            display: { xs: "inline-flex", lg: "block" },
                          }}
                        />
                      )}
                      {user.status === "invited" && (
                        <Chip
                          label="Invited"
                          size="small"
                          color="warning"
                          sx={{
                            fontWeight: 700,
                            mb: { xs: 0, lg: 0.5 },
                            display: { xs: "inline-flex", lg: "block" },
                          }}
                        />
                      )}
                      <Chip
                        label={
                          isGlobal
                            ? "Global Admin"
                            : `${grantedCount} / ${ALL_PERMISSION_KEYS.length} pages`
                        }
                        size="small"
                        color={isGlobal ? "success" : "default"}
                        sx={{ fontWeight: 700 }}
                      />
                    </MDBox>
                  </MDBox>

                  {user.disabled && (
                    <MDBox
                      mb={2}
                      sx={{
                        background: lightDark("#fdecea", darkTint("#f44335")),
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        color="error"
                        fontWeight="bold"
                        display="block"
                      >
                        Can&apos;t sign in
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary" display="block">
                        Disabled
                        {user.disabledAt
                          ? ` on ${new Date(user.disabledAt).toLocaleDateString(undefined, {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}`
                          : ""}
                        . Their permissions are kept for when they&apos;re enabled again.
                      </MDTypography>
                    </MDBox>
                  )}

                  {/* Global admin toggle */}
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={2}
                    sx={{
                      background: isGlobal
                        ? lightDark("#e8f5e9", darkTint("#4caf50"))
                        : lightDark("#f5f5f5", DARK.surface),
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                    }}
                  >
                    {/* Your own Global Admin can't be switched off (the server refuses it too):
                        it would lock you out of this page. */}
                    <Switch
                      checked={isGlobal}
                      onChange={(e) => handleGlobalAdminChange(user, e.target.checked)}
                      disabled={isSelf && isGlobal}
                      color="success"
                    />
                    <MDBox flex={1}>
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        color={isGlobal ? "success.main" : "text"}
                      >
                        Global Administrator
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary" display="block">
                        {isSelf && isGlobal
                          ? "That's you. Another global admin can change this; you can't remove your own access."
                          : isGlobal
                          ? "Full unrestricted access to all pages"
                          : "Access limited to enabled pages below"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* Chat notification subscription — independent of page permissions.
                      Admin decides who gets emailed for new Twilio messages. */}
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={2}
                    sx={{
                      background: user.notifyOnTwilioMessage
                        ? lightDark("#e3f2fd", darkTint("#1a73e8"))
                        : lightDark("#f5f5f5", DARK.surface),
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Switch
                      checked={!!user.notifyOnTwilioMessage}
                      onChange={(e) => handleNotifyToggleChange(user, e.target.checked)}
                      color="info"
                    />
                    <MDBox flex={1}>
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        color={user.notifyOnTwilioMessage ? "info.main" : "text"}
                      >
                        Notify on new chat messages
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary" display="block">
                        Emails this user every time a customer sends a new SMS or WhatsApp message
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* Missed-call notification subscription — independent of page permissions.
                      Admin decides who gets emailed when an inbound call goes unanswered and a
                      voicemail is left. */}
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={2}
                    sx={{
                      background: user.notifyOnMissedCall
                        ? lightDark("#e3f2fd", darkTint("#1a73e8"))
                        : lightDark("#f5f5f5", DARK.surface),
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Switch
                      checked={!!user.notifyOnMissedCall}
                      onChange={(e) => handleMissedCallNotifyToggleChange(user, e.target.checked)}
                      color="info"
                    />
                    <MDBox flex={1}>
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        color={user.notifyOnMissedCall ? "info.main" : "text"}
                      >
                        Notify on missed calls
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary" display="block">
                        Emails this user every time an inbound call goes unanswered and a voicemail
                        is left
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* Quick actions */}
                  {!isGlobal && (
                    <MDBox display="flex" gap={1} mb={2}>
                      <MDButton
                        size="small"
                        variant="outlined"
                        color="success"
                        sx={touchButtonSx}
                        onClick={() => handleToggleAll(user, true)}
                      >
                        Grant All
                      </MDButton>
                      <MDButton
                        size="small"
                        variant="outlined"
                        color="error"
                        sx={touchButtonSx}
                        onClick={() => handleToggleAll(user, false)}
                      >
                        Revoke All
                      </MDButton>
                    </MDBox>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  {/* Permission groups */}
                  {PAGE_GROUPS.map((group) => (
                    <MDBox key={group.label} mb={1.5}>
                      <MDBox display="flex" alignItems="center" gap={0.5} mb={0.75}>
                        <Icon
                          sx={{
                            fontSize: "0.9rem !important",
                            color: lightDark(group.color, group.darkColor),
                          }}
                        >
                          {group.icon}
                        </Icon>
                        <MDTypography
                          variant="overline"
                          sx={{
                            fontSize: "0.65rem",
                            letterSpacing: 1,
                            color: lightDark(group.color, group.darkColor),
                            fontWeight: 700,
                          }}
                        >
                          {group.label}
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" flexWrap="wrap" gap={0.75} sx={permissionChipsSx}>
                        {group.pages.map(({ key, label }) => {
                          const granted = isGlobal || user.permissions?.[key] === true;
                          const locked = isGlobal;
                          return (
                            <Tooltip
                              key={key}
                              title={
                                locked
                                  ? "Global admin — always granted"
                                  : granted
                                  ? "Click to revoke"
                                  : "Click to grant"
                              }
                            >
                              <Chip
                                label={label}
                                size="small"
                                color={granted ? "info" : "default"}
                                variant={granted ? "filled" : "outlined"}
                                clickable={!locked}
                                onClick={
                                  !locked
                                    ? () => handlePermissionChange(user, key, !granted)
                                    : undefined
                                }
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.72rem",
                                  opacity: locked ? 0.55 : 1,
                                  cursor: locked ? "default" : "pointer",
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                      </MDBox>
                    </MDBox>
                  ))}

                  {/* Always-on defaults notice — derived from DEFAULT_PAGES so this
                      text can't drift out of sync with what's actually enforced. */}
                  <MDBox
                    mt={1}
                    mb={2}
                    sx={{
                      background: lightDark("#f0f4ff", darkTint("#1a73e8", 0.1)),
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.75,
                    }}
                  >
                    <MDTypography variant="caption" color="text.secondary">
                      Always granted:{" "}
                      <strong>
                        {DEFAULT_PAGES.map((p) => p[0].toUpperCase() + p.slice(1)).join(" · ")}
                      </strong>
                    </MDTypography>
                  </MDBox>

                  {hasDraft(drafts, user._id) && (
                    <MDTypography
                      variant="caption"
                      color="warning"
                      fontWeight="bold"
                      display="block"
                      mb={1}
                    >
                      Unsaved changes. Press Save Permissions to apply them.
                    </MDTypography>
                  )}
                  <MDButton
                    fullWidth
                    size="small"
                    color="info"
                    variant="gradient"
                    sx={touchButtonSx}
                    startIcon={
                      saving === user._id ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    onClick={() => handleSave(user)}
                    disabled={saving === user._id}
                  >
                    {saving === user._id ? "Saving…" : "Save Permissions"}
                  </MDButton>

                  {user.status === "invited" && (
                    <MDButton
                      fullWidth
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={[{ mt: 1 }, touchButtonSx]}
                      startIcon={
                        resending === user._id ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <Icon>mail</Icon>
                        )
                      }
                      onClick={() => handleResendInvite(user)}
                      disabled={resending === user._id}
                    >
                      {resending === user._id ? "Resending…" : "Resend Invite"}
                    </MDButton>
                  )}

                  {currentUser?._id !== user._id && (
                    <MDButton
                      fullWidth
                      size="small"
                      color={user.disabled ? "success" : "error"}
                      variant="outlined"
                      sx={[{ mt: 1 }, touchButtonSx]}
                      startIcon={<Icon>{user.disabled ? "lock_open" : "block"}</Icon>}
                      onClick={() => setAccessTarget(user)}
                    >
                      {user.disabled ? "Enable User" : "Disable User"}
                    </MDButton>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Pagination. Unsaved edits stay in `drafts` across pages. */}
        {unsavedElsewhere > 0 && (
          <MDTypography
            variant="caption"
            color="warning"
            fontWeight="bold"
            display="block"
            textAlign="center"
            mt={3}
          >
            {unsavedElsewhere === 1
              ? "1 user on another page has"
              : `${unsavedElsewhere} users on other pages have`}{" "}
            unsaved changes. They&apos;re kept until you save them or leave this screen.
          </MDTypography>
        )}
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={unsavedElsewhere > 0 ? 1.5 : 4}
          gap={2}
        >
          <MDButton
            variant="outlined"
            color="info"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </MDButton>
          <MDTypography variant="button" color="text">
            Page {page} of {totalPages}
          </MDTypography>
          <MDButton
            variant="outlined"
            color="info"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </MDButton>
        </MDBox>

        {/* Disable / enable confirmation */}
        <Dialog
          open={!!accessTarget}
          onClose={() => !accessSaving && setAccessTarget(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {accessTarget?.disabled ? "Enable" : "Disable"}{" "}
            {accessTarget?.name || accessTarget?.email}?
          </DialogTitle>
          <DialogContent>
            <MDTypography variant="body2" color="text">
              {accessTarget?.disabled
                ? "They'll be able to sign in again, with the same permissions as before."
                : "They'll be signed out straight away and won't be able to sign in until you enable them again. Their permissions are kept."}
            </MDTypography>
          </DialogContent>
          <DialogActions>
            <MDButton
              onClick={() => setAccessTarget(null)}
              color="secondary"
              variant="outlined"
              disabled={accessSaving}
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={handleConfirmAccess}
              color={accessTarget?.disabled ? "success" : "error"}
              variant="gradient"
              disabled={accessSaving}
            >
              {accessSaving ? (
                <CircularProgress size={18} color="inherit" />
              ) : accessTarget?.disabled ? (
                "Enable User"
              ) : (
                "Disable User"
              )}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Add User Modal */}
        {addUserOpen && (
          <MDBox
            position="fixed"
            top={0}
            left={0}
            width="100vw"
            height="100vh"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={1300}
            sx={{ background: "rgba(0,0,0,0.3)" }}
          >
            <Card
              sx={{
                p: { xs: 3, sm: 4 },
                mx: 2,
                width: "100%",
                maxWidth: 420,
                borderRadius: 3,
                boxShadow: 8,
              }}
            >
              <MDTypography variant="h5" fontWeight="bold" mb={1} textAlign="center">
                Invite User
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text.secondary"
                display="block"
                textAlign="center"
                mb={3}
              >
                They&apos;ll get an email with a link to accept, then sign in with the Microsoft or
                Google account for that address.
              </MDTypography>
              <form onSubmit={handleAddUserSubmit}>
                {[
                  {
                    label: "Full Name",
                    name: "name",
                    type: "text",
                    placeholder: "e.g. Jane Smith",
                    required: true,
                  },
                  {
                    label: "Email Address",
                    name: "email",
                    type: "email",
                    placeholder: "e.g. jane.smith@elite247hub.com",
                    required: true,
                  },
                ].map(({ label, ...props }) => (
                  <MDBox key={props.name} mb={2}>
                    <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                      {label}
                      {/* Both fields are required (the server refuses an invite without a name). */}
                      <MDTypography
                        component="span"
                        variant="caption"
                        color="error"
                        fontWeight="bold"
                      >
                        {" "}
                        *
                      </MDTypography>
                    </MDTypography>
                    <input
                      {...props}
                      value={addUserForm[props.name]}
                      onChange={handleAddUserChange}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: 6,
                        border: "1px solid #ccc",
                        fontSize: 16,
                      }}
                    />
                  </MDBox>
                ))}
                <MDBox mb={3} display="flex" alignItems="center" gap={1}>
                  <Checkbox
                    name="isGlobalAdmin"
                    checked={addUserForm.isGlobalAdmin}
                    onChange={handleAddUserChange}
                    color="info"
                  />
                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold">
                      Global Admin
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" display="block">
                      Grants full unrestricted access
                    </MDTypography>
                  </MDBox>
                </MDBox>
                {addUserError && (
                  <MDTypography color="error" mb={1} variant="caption">
                    {addUserError}
                  </MDTypography>
                )}
                <MDBox display="flex" justifyContent="flex-end" gap={1.5} mt={1}>
                  <MDButton
                    onClick={handleAddUserClose}
                    color="secondary"
                    variant="outlined"
                    disabled={addUserLoading}
                  >
                    Cancel
                  </MDButton>
                  <MDButton type="submit" color="info" variant="gradient" disabled={addUserLoading}>
                    {addUserLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      "Send Invite"
                    )}
                  </MDButton>
                </MDBox>
              </form>
            </Card>
          </MDBox>
        )}

        <MDSnackbar
          color="success"
          icon="check"
          title={notice?.title || ""}
          content={notice?.content || ""}
          dateTime=""
          open={!!notice}
          onClose={() => setNotice(null)}
          close={() => setNotice(null)}
          bgWhite
        />
      </MDBox>
    </DashboardLayout>
  );
};

export default ManageUsers;
